import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  LocalPolice as CuratorIcon,
  BusinessCenter as ClientIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

// Mapeamento de perfis de acesso
const ROLE_MAP = {
  admin: { label: 'Administrador', color: 'error', icon: <AdminIcon fontSize="small" /> },
  mensure: { label: 'Mensure Team', color: 'secondary', icon: <AdminIcon fontSize="small" /> },
  curator: { label: 'Curador', color: 'info', icon: <CuratorIcon fontSize="small" /> },
  client: { label: 'Cliente', color: 'primary', icon: <ClientIcon fontSize="small" /> }
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // States para a Tabela e Pesquisa
  const [searchTerm, setSearchTerm] = useState('');
  
  // States para os Modais
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form de Edição
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: 'client'
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/all');
      if (res.data.success) {
        setUsers(res.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar lista de usuários');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Ações
  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role
    });
    setEditDialogOpen(true);
  };

  const handleOpenDelete = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatus = async (user) => {
    // Evita o autogolpe de bloqueio do próprio admin
    if (user._id === currentUser._id) {
      setError('Você não pode bloquear sua própria conta ativa em uso.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    try {
      const res = await api.put(`/users/${user._id}/status`);
      if (res.data.success) {
        setSuccess(`Usuário ${user.name} foi ${!user.isActive ? 'desbloqueado' : 'bloqueado'}!`);
        // Atualiza a linha localmente pra ser mais rápido
        setUsers(users.map(u => u._id === user._id ? { ...u, isActive: !u.isActive } : u));
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar status do usuário');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/users/${selectedUser._id}`, editForm);
      if (res.data.success) {
        setSuccess('Usuário atualizado com sucesso!');
        setEditDialogOpen(false);
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar usuário');
      setTimeout(() => setError(''), 3000);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/users/${selectedUser._id}`);
      if (res.data.success) {
        setSuccess('Usuário excluído permanentemente!');
        setDeleteDialogOpen(false);
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir usuário');
      setTimeout(() => setError(''), 3000);
      setDeleteDialogOpen(false);
    }
  };

  // Filtragem local
  const filteredUsers = users.filter(u => 
    !searchTerm || 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} color="text.primary">Gestão de Usuários</Typography>
          <Typography variant="body2" color="text.secondary">
            Administre perfis, acessos e bloqueie credenciais.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchUsers} disabled={loading}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            placeholder="Buscar por nome ou e-mail..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredUsers.length}
            </Typography>
          </Box>
        </Box>
        
        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}></TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Acesso (Role)</TableCell>
                <TableCell>Criado Em</TableCell>
                <TableCell align="center">Liberado / Bloqueado</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><Typography color="text.secondary">Nenhum usuário encontrado.</Typography></TableCell></TableRow>
              ) : (
                filteredUsers.map((u) => {
                  const roleDef = ROLE_MAP[u.role] || ROLE_MAP['client'];
                  const isMe = u._id === currentUser?._id;
                  
                  return (
                    <TableRow key={u._id} hover sx={{ opacity: u.isActive !== false ? 1 : 0.6 }}>
                      <TableCell>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: `${roleDef.color}.main`, opacity: u.isActive !== false ? 1 : 0.5 }}>
                          {u.name?.charAt(0).toUpperCase()}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {u.name} {isMe && <Chip label="Você" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={roleDef.icon} 
                          label={roleDef.label} 
                          size="small" 
                          color={roleDef.color}
                          variant={u.role === 'admin' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={isMe ? "Você não pode bloquear a si mesmo" : (u.isActive !== false ? "Bloquear Acesso" : "Desbloquear Acesso")}>
                          <span>
                            <Switch 
                              checked={u.isActive !== false} 
                              onChange={() => handleToggleStatus(u)} 
                              size="small"
                              disabled={saving || isMe}
                              color={u.isActive !== false ? "success" : "error"}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar Permissões/Dados">
                          <IconButton size="small" onClick={() => handleOpenEdit(u)} disabled={saving}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isMe ? "Você não pode se excluir" : "Excluir Conta Permanentemente"}>
                          <span>
                            <IconButton size="small" onClick={() => handleOpenDelete(u)} disabled={saving || isMe} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog: Edit User (Padronizado) */}
      <PlatformDialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Editar Usuário"
          subtitle="Gerencie permissões e dados básicos do colaborador."
          icon={<UserIcon />}
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
              Ao alterar o tipo de acesso, o usuário recebe/perde privilégios de administrador instantaneamente.
            </Alert>
            <TextField
              label="Nome Real"
              value={editForm.name}
              onChange={(e) => setEditForm({...editForm, name: e.target.value})}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Endereço de E-mail"
              value={editForm.email}
              onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Nível de Acesso (Role)</InputLabel>
              <Select
                value={editForm.role}
                label="Nível de Acesso (Role)"
                onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                sx={{ borderRadius: 2 }}
              >
                {Object.keys(ROLE_MAP).map(roleKey => (
                  <MenuItem key={roleKey} value={roleKey}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {ROLE_MAP[roleKey].icon} {ROLE_MAP[roleKey].label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit} 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog: Delete Confirmation (Padronizado) */}
      <PlatformDialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Excluir Usuário?"
          subtitle="Esta ação é definitiva e removerá o acesso."
          icon={<DeleteIcon />}
          onClose={() => setDeleteDialogOpen(false)}
          color="error"
        />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Excluir o usuário <strong>{selectedUser?.name}</strong> removerá seus dados de acesso ao sistema.
          </Typography>
          <Alert severity="error" variant="filled" sx={{ mt: 2, borderRadius: 2 }}>
            Isto não pode ser desfeita. Marcas ligadas a ele ficarão órfãs.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete} 
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : 'Excluir Conta'}
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default UserManagement;
